<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

const props = withDefaults(
  defineProps<{
    // Inner content max width. Default fits typical pages; `wide` and `full`
    // give table-heavy views room to breathe.
    width?: 'default' | 'wide' | 'full';
    // Collapse the desktop sidebar to an icon rail.
    collapsed?: boolean;
  }>(),
  { width: 'default', collapsed: false },
);

const innerWidthClass = computed(() => {
  if (props.width === 'full') return 'max-w-none';
  if (props.width === 'wide') return 'max-w-screen-2xl';
  return 'max-w-6xl';
});

const showSidebar = ref(false);
function close() {
  showSidebar.value = false;
}
function open() {
  showSidebar.value = true;
}
</script>

<template>
  <div class="relative isolate flex min-h-svh w-full bg-white max-lg:flex-col lg:bg-zinc-100 dark:bg-zinc-900 dark:lg:bg-zinc-950">
    <!-- Sidebar on desktop -->
    <div
      :class="[
        'fixed inset-y-0 left-0 max-lg:hidden transition-[width] duration-200',
        props.collapsed ? 'w-16' : 'w-64',
      ]"
    >
      <slot name="sidebar" />
    </div>

    <!-- Sidebar on mobile -->
    <TransitionRoot :show="showSidebar" as="template">
      <Dialog class="lg:hidden" @close="close">
        <TransitionChild
          as="template"
          enter="transition duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/30" />
        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transition duration-300 ease-in-out"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition duration-300 ease-in-out"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <DialogPanel class="fixed inset-y-0 w-full max-w-80 p-2">
            <div class="flex h-full flex-col rounded-lg bg-white shadow-xs ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
              <div class="-mb-3 px-4 pt-3">
                <button
                  type="button"
                  class="relative flex items-center gap-3 rounded-lg p-2 text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/5"
                  aria-label="Close navigation"
                  @click="close"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true" class="size-6 fill-current">
                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                  </svg>
                </button>
              </div>
              <slot name="sidebar" />
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </TransitionRoot>

    <!-- Navbar on mobile -->
    <header class="flex items-center px-4 lg:hidden">
      <div class="py-2.5">
        <button
          type="button"
          class="relative flex items-center gap-3 rounded-lg p-2 text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/5"
          aria-label="Open navigation"
          @click="open"
        >
          <svg viewBox="0 0 20 20" aria-hidden="true" class="size-6 fill-current">
            <path d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z" />
          </svg>
        </button>
      </div>
      <div class="min-w-0 flex-1">
        <slot name="navbar" />
      </div>
    </header>

    <!-- Content -->
    <main
      :class="[
        'flex flex-1 flex-col pb-2 lg:min-w-0 lg:pt-2 lg:pr-2 transition-[padding] duration-200',
        props.collapsed ? 'lg:pl-16' : 'lg:pl-64',
      ]"
    >
      <div class="grow p-6 lg:rounded-lg lg:bg-white lg:p-10 lg:shadow-xs lg:ring-1 lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10">
        <div :class="['mx-auto', innerWidthClass]">
          <slot />
        </div>
      </div>
    </main>
  </div>
</template>
